import {AfterViewInit, Component, computed, effect, inject, OnDestroy, signal, viewChild} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {GridsterComponent, GridsterConfig, GridsterItem, GridsterItemComponent} from "angular-gridster2";
import {InputText} from "primeng/inputtext";
import {DatasetStore, ElementContentComponent, WidgetElement} from "@entities/widget-element";
import {WidgetConstructorStore} from "../../store/widget-constructor-store";
import {WidgetConstructorMapperService} from "../../lib/widget-constructor.mapper.service";
import {Button} from "primeng/button";

@Component({
    selector: "widget-constructor",
    imports: [
        InputText,
        ElementContentComponent,
        GridsterComponent,
        GridsterItemComponent,
        Button
    ],
    templateUrl: "./widget-grid.component.html",
    styleUrl: "./widget-grid.component.scss"
})
export class WidgetGridComponent implements AfterViewInit, OnDestroy {
    readonly grid = viewChild(GridsterComponent);
    readonly router = inject(Router);
    readonly route = inject(ActivatedRoute);
    readonly store = inject(WidgetConstructorStore);
    readonly datasetStore = inject(DatasetStore);
    readonly mapper = inject(WidgetConstructorMapperService);
    private resizeObserver?: ResizeObserver;

    readonly isDragging = signal(false);
    readonly selectedElement = computed(() => this.store.editingElement());
    readonly gridElements = computed<GridsterItem[]>(() => this.mapper.mapWidgetElementsToGridsterItems(this.elements()));
    readonly elements = computed<WidgetElement[]>(() => Object.values(this.data().elements));
    readonly data = computed(() => this.store.editingWidget()!);
    readonly isElementLoading = computed(() => {
        const id = this.selectedElement()?.id || "";
        const elementData = this.datasetStore.datesetEntityMap()[id];
        if (elementData) {
            return elementData.isFetching;
        }

        return false;
    });

    options: GridsterConfig = {
        maxCols: 12,
        maxRows: 12,
        draggable: {
            enabled: true,
            start: () => this.isDragging.set(true),
            stop: () => this.isDragging.set(false)
        },
        resizable: {
            enabled: true
        },
        swap: true,
        displayGrid: "always",
        itemChangeCallback: () => {
            this.updateElements();
            this.handleFirstPossiblePositionChange();
        },
        mobileBreakpoint: 0
    };

    changePossiblePosition = effect(() => {
        if (this.elements()) {
            setTimeout(() => {
                this.handleFirstPossiblePositionChange();
            });
        }
    });

    ngAfterViewInit() {
        this.handleGridResize();
        this.handleFirstPossiblePositionChange();
    }

    ngOnDestroy() {
        this.resizeObserver?.disconnect();
    }

    toggleSelectedElement(element: WidgetElement) {
        if (!this.isDragging()) {
            if (this.selectedElement()?.id === element.id) {
                this.store.resetEditingElement(); // deselect
            } else {
                this.store.setEditingElement(element); // select
            }
        }
    }

    updateEditingElementName(name: string) {
        this.store.updateEditingElementContent({name});
    }

    removeElement(id: string) {
        this.store.removeEditingWidgetElement(id);
    }

    private updateElements() {
        const items = this.mapper.mapGridsterItemComponentsToWidgetElements(this.grid()!.grid);
        this.store.updateEditingWidgetElements(items);
    }

    private handleGridResize() {
        this.resizeObserver = new ResizeObserver(() => {
            if (this.options.api && this.options.api.resize) {
                this.options.api.resize();
            }
        });

        const container = this.grid()?.el.parentElement!;
        this.resizeObserver.observe(container);
    }

    private handleFirstPossiblePositionChange() {
        if (this.options.api && this.options.api.getFirstPossiblePosition) {
            const {x, y} = this.options.api.getFirstPossiblePosition({x: 0, y: 0, cols: 1, rows: 1});
            this.store.setFirstPossiblePosition({x, y});
        }
    }
}
